import { DomainEvent } from '../events/GameEvents';
import { MoveCommand } from '../mechanics/MoveCommand';
/**
 * Game flow phase changed event
 */
export class GameFlowPhaseChangedEvent extends DomainEvent {
    phase;
    previousPhase;
    constructor(phase, previousPhase) {
        super();
        this.phase = phase;
        this.previousPhase = previousPhase;
    }
    toJSON() {
        return {
            eventType: this.eventType,
            eventId: this.eventId,
            timestamp: this.timestamp,
            phase: this.phase,
            previousPhase: this.previousPhase,
        };
    }
}
/**
 * Move flow completed event
 */
export class MoveFlowCompletedEvent extends DomainEvent {
    result;
    nextSide;
    constructor(result, nextSide) {
        super();
        this.result = result;
        this.nextSide = nextSide;
    }
    toJSON() {
        return {
            eventType: this.eventType,
            eventId: this.eventId,
            timestamp: this.timestamp,
            result: this.result,
            nextSide: this.nextSide,
        };
    }
}
/**
 * Game phase states for the state machine
 */
export var GamePhase;
(function (GamePhase) {
    GamePhase["SETUP"] = "setup";
    GamePhase["PLAYING"] = "playing";
    GamePhase["PAUSED"] = "paused";
    GamePhase["ENDED"] = "ended";
})(GamePhase || (GamePhase = {}));
/**
 * Flow states within the PLAYING phase
 */
export var FlowState;
(function (FlowState) {
    FlowState["WAITING_FOR_MOVE"] = "WAITING_FOR_MOVE";
    FlowState["VALIDATING_MOVE"] = "VALIDATING_MOVE";
    FlowState["EXECUTING_MOVE"] = "EXECUTING_MOVE";
    FlowState["PROCESSING_EFFECTS"] = "PROCESSING_EFFECTS";
    FlowState["CHECKING_END_CONDITIONS"] = "CHECKING_END_CONDITIONS";
})(FlowState || (FlowState = {}));
/**
 * Game Flow Controller
 *
 * Manages game flow using State Machine pattern for phases and turns.
 * Coordinates with TurnManager and RoundManager for game progression.
 * Validates state transitions and handles game flow events.
 *
 * Maximum 200 lines - focused on flow control logic only.
 */
export class GameFlowController {
    board;
    turnManager;
    roundManager;
    moveExecutor;
    eventBus;
    currentPhase = GamePhase.SETUP;
    currentFlowState = FlowState.WAITING_FOR_MOVE;
    // Valid phase transitions
    phaseTransitions = new Map([
        [GamePhase.SETUP, [GamePhase.PLAYING]],
        [GamePhase.PLAYING, [GamePhase.PAUSED, GamePhase.ENDED]],
        [GamePhase.PAUSED, [GamePhase.PLAYING, GamePhase.ENDED]],
        [GamePhase.ENDED, []], // Terminal state
    ]);
    // Valid flow state transitions within PLAYING phase
    flowTransitions = new Map([
        [FlowState.WAITING_FOR_MOVE, [FlowState.VALIDATING_MOVE]],
        [FlowState.VALIDATING_MOVE, [FlowState.EXECUTING_MOVE, FlowState.WAITING_FOR_MOVE]],
        [FlowState.EXECUTING_MOVE, [FlowState.PROCESSING_EFFECTS, FlowState.WAITING_FOR_MOVE]],
        [FlowState.PROCESSING_EFFECTS, [FlowState.CHECKING_END_CONDITIONS]],
        [FlowState.CHECKING_END_CONDITIONS, [FlowState.WAITING_FOR_MOVE]],
    ]);
    constructor(board, turnManager, roundManager, moveExecutor, eventBus) {
        this.board = board;
        this.turnManager = turnManager;
        this.roundManager = roundManager;
        this.moveExecutor = moveExecutor;
        this.eventBus = eventBus;
        this.subscribeToEvents();
    }
    /**
     * Start the game flow
     */
    start() {
        this.validatePhaseTransition(GamePhase.PLAYING);
        this.currentPhase = GamePhase.PLAYING;
        this.currentFlowState = FlowState.WAITING_FOR_MOVE;
        this.eventBus.publish(new GameFlowPhaseChangedEvent(this.currentPhase));
    }
    /**
     * Pause the game flow
     */
    pause() {
        if (this.currentPhase !== GamePhase.PLAYING) {
            throw new Error(`Invalid phase transition: cannot pause from ${this.currentPhase}`);
        }
        this.validatePhaseTransition(GamePhase.PAUSED);
        this.currentPhase = GamePhase.PAUSED;
        this.eventBus.publish(new GameFlowPhaseChangedEvent(this.currentPhase));
    }
    /**
     * Resume the game flow
     */
    resume() {
        if (this.currentPhase !== GamePhase.PAUSED) {
            throw new Error(`Invalid phase transition: cannot resume from ${this.currentPhase}`);
        }
        this.validatePhaseTransition(GamePhase.PLAYING);
        this.currentPhase = GamePhase.PLAYING;
        this.currentFlowState = FlowState.WAITING_FOR_MOVE;
        this.eventBus.publish(new GameFlowPhaseChangedEvent(this.currentPhase));
    }
    /**
     * End the game
     */
    end() {
        if (this.currentPhase === GamePhase.ENDED) {
            return; // Already ended
        }
        this.validatePhaseTransition(GamePhase.ENDED);
        this.currentPhase = GamePhase.ENDED;
        this.eventBus.publish(new GameFlowPhaseChangedEvent(this.currentPhase));
    }
    /**
     * Execute a move with full flow state management
     */
    async executeMove(from, to) {
        const currentSide = this.turnManager.getCurrentSide();
        return await this.executeMoveForSide(from, to, currentSide);
    }
    /**
     * Execute a move for a specific side with state machine validation
     */
    async executeMoveForSide(from, to, sideId) {
        // Validate game can accept moves
        if (!this.canAcceptMoves()) {
            return this.createFailedResult(from, to, sideId, `Game is in ${this.currentPhase} phase and cannot accept moves`);
        }
        // Transition through flow states
        this.transitionFlowState(FlowState.VALIDATING_MOVE);
        const validation = this.validateMoveConditions(sideId);
        if (!validation.valid) {
            this.transitionFlowState(FlowState.WAITING_FOR_MOVE);
            return this.createFailedResult(from, to, sideId, validation.error);
        }
        this.transitionFlowState(FlowState.EXECUTING_MOVE);
        let result;
        try {
            result = await this.performMove(from, to, sideId);
        }
        catch (error) {
            // Handle execution errors gracefully
            console.error(`[GameFlowController] ❌ Error during move execution: ${error.message}`);
            console.error(`[GameFlowController] Stack trace:`, error.stack);
            this.transitionFlowState(FlowState.WAITING_FOR_MOVE);
            return this.createFailedResult(from, to, sideId, error.message);
        }
        if (result.success) {
            this.transitionFlowState(FlowState.PROCESSING_EFFECTS);
            await this.processSuccessfulMove(result);
            this.transitionFlowState(FlowState.CHECKING_END_CONDITIONS);
            this.checkGameEndConditions();
            this.transitionFlowState(FlowState.WAITING_FOR_MOVE);
        }
        else {
            // On failure, go back to waiting for move
            this.transitionFlowState(FlowState.WAITING_FOR_MOVE);
        }
        return result;
    }
    /**
     * Validate move conditions before execution
     */
    validateMoveConditions(sideId) {
        const currentSide = this.turnManager.getCurrentSide();
        if (sideId !== currentSide) {
            return {
                valid: false,
                canProceed: false,
                error: `Not side ${sideId}'s turn (current: ${currentSide})`,
            };
        }
        if (!this.roundManager.canMakeMove(sideId)) {
            const round = this.roundManager.getCurrentRound();
            console.log(`[GameFlowController.validateMove] Side ${sideId} cannot move in round ${round}`);
            return {
                valid: false,
                canProceed: false,
                error: `Side ${sideId} has no remaining moves this round`,
            };
        }
        return { valid: true, canProceed: true };
    }
    /**
     * Process effects after successful move
     */
    async processSuccessfulMove(result) {
        const sideId = result.command.sideId;
        // ✅ ПРОБЛЕМА 4: Проверяем, не сбежал ли игрок из отложенных ловушек
        // Побег = окружение разомкнуто (сделал ход, который открыл проход)
        const escapedTraps = this.moveExecutor.checkPendingTrapEscapes(sideId);
        if (escapedTraps.length > 0) {
            console.log(`[GameFlowController] 🏃 Side ${sideId} escaped from ${escapedTraps.length} trap(s)!`);
        }
        const cancelledGroupConquests = this.moveExecutor.checkPendingGroupConquestEscapes(sideId);
        if (cancelledGroupConquests.length > 0) {
            console.log(`[GameFlowController] 🏃 Side ${sideId} broke free from ${cancelledGroupConquests.length} pending group conquest(s)!`);
        }
        // Проверяем, должен ли закончиться ход этой стороны
        // (исчерпаны ходы или нет доступных позиций)
        const willEndTurn = this.turnManager.willEndTurn();
        // ✅ ПРОБЛЕМА 4: Если ход стороны заканчивается, разрешаем все pending traps
        // Track positions resolved here so the board-wide sweep doesn't immediately
        // recapture territories given to a reanimated (formerly eliminated/suspended) side.
        const resolvedTrapPositions = new Set();
        if (willEndTurn && this.moveExecutor.hasPendingTraps(sideId)) {
            console.log(`[GameFlowController] 🔒 Turn ending for side ${sideId} - resolving pending traps`);
            const resolutions = this.moveExecutor.resolvePendingTraps(sideId, resolvedTrapPositions);
            console.log(`[GameFlowController] Resolved ${resolutions.length} pending trap(s)`);
        }
        if (willEndTurn && this.moveExecutor.hasPendingGroupConquests(sideId)) {
            console.log(`[GameFlowController] 🔒 Turn ending for side ${sideId} - resolving pending group conquests`);
            this.moveExecutor.resolvePendingGroupConquests(sideId);
        }
        if (willEndTurn) {
            const boardWideCaptures = await this.moveExecutor.resolveBoardWideCapturesAtTurnEnd(resolvedTrapPositions);
            if (boardWideCaptures > 0) {
                console.log(`[GameFlowController] 🌐 End-turn board sweep captured ${boardWideCaptures} territory(ies)`);
            }
        }
        const canContinue = this.turnManager.endTurn();
        // Safety net: willEndTurn() is a prediction made before endTurn(). If the turn
        // actually switched (e.g. no valid moves remained), resolve any pending traps
        // and group conquests that the prediction-based checks above may have missed.
        const turnActuallySwitched = this.turnManager.getCurrentSide() !== sideId;
        if (turnActuallySwitched) {
            if (this.moveExecutor.hasPendingTraps(sideId)) {
                console.log(`[GameFlowController] 🔒 Safety net: resolving pending traps for side ${sideId} after turn switch`);
                const resolutions = this.moveExecutor.resolvePendingTraps(sideId);
                console.log(`[GameFlowController] Safety net resolved ${resolutions.length} pending trap(s)`);
            }
            if (this.moveExecutor.hasPendingGroupConquests(sideId)) {
                console.log(`[GameFlowController] 🔒 Safety net: resolving pending group conquests for side ${sideId} after turn switch`);
                this.moveExecutor.resolvePendingGroupConquests(sideId);
            }
        }
        // Always publish move completed event for other systems
        this.eventBus.publish(new MoveFlowCompletedEvent(result, this.turnManager.getCurrentSide()));
        if (!canContinue) {
            this.end();
        }
    }
    /**
     * Check if game should end based on conditions
     */
    checkGameEndConditions() {
        if (!this.turnManager.canContinue()) {
            this.end();
        }
    }
    // === State Management Methods ===
    /**
     * Get current game phase
     */
    getCurrentPhase() {
        return this.currentPhase;
    }
    /**
     * Get current flow state (within PLAYING phase)
     */
    getCurrentFlowState() {
        return this.currentFlowState;
    }
    /**
     * Check if game can accept moves
     */
    canAcceptMoves() {
        return (this.currentPhase === GamePhase.PLAYING &&
            this.currentFlowState === FlowState.WAITING_FOR_MOVE);
    }
    // === Private Helper Methods ===
    /**
     * Validate phase transitions are allowed
     */
    validatePhaseTransition(targetPhase) {
        const allowedTransitions = this.phaseTransitions.get(this.currentPhase) || [];
        if (!allowedTransitions.includes(targetPhase)) {
            throw new Error(`Invalid phase transition from ${this.currentPhase} to ${targetPhase}`);
        }
    }
    /**
     * Transition between flow states with validation
     */
    transitionFlowState(targetState) {
        if (this.currentPhase !== GamePhase.PLAYING) {
            return; // Flow states only valid in PLAYING phase
        }
        const allowedTransitions = this.flowTransitions.get(this.currentFlowState) || [];
        if (!allowedTransitions.includes(targetState)) {
            throw new Error(`Invalid flow transition from ${this.currentFlowState} to ${targetState}`);
        }
        this.currentFlowState = targetState;
    }
    /**
     * Perform the actual move execution
     */
    async performMove(from, to, sideId) {
        const round = this.roundManager.getCurrentRound();
        const side = this.board.getSide(sideId);
        const moveNumber = (side?.movesInCurrentRound || 0) + 1;
        // Check if this will be the last move BEFORE executing (for group surrender popup logic).
        const isLastMove = this.turnManager.willEndTurn();
        const command = new MoveCommand(sideId, from, to, round, moveNumber);
        return await this.moveExecutor.execute(command, isLastMove);
    }
    /**
     * Create failed move result
     */
    createFailedResult(from, to, sideId, error) {
        return {
            success: false,
            command: new MoveCommand(sideId, from, to, this.roundManager.getCurrentRound(), 0),
            error,
        };
    }
    /**
     * Subscribe to relevant game events
     */
    subscribeToEvents() {
        // Listen for turn manager events that might affect flow
        this.eventBus.subscribe('TURN_CHANGED', () => {
            if (this.currentPhase === GamePhase.PLAYING) {
                this.currentFlowState = FlowState.WAITING_FOR_MOVE;
            }
        });
    }
}
//# sourceMappingURL=GameFlowController.js.map